import React, { useRef } from "react";
import { Card, Col } from "react-bootstrap";
import Chart from "react-apexcharts";
import { findIcon } from "../../views/dashboard/icons";
import Skeleton from "../skeleton";
import PageNotFound from "../../views/PageNotFound";
import Select from "react-select";
import { GraphCardsHeader } from "@ui";

function LineChart({
  seriesName = "Viewed Time (Mins)",
  series,
  labels,
  height = 300,
  markerSize,
  chartTitle,
  activeCampaignGroup,
  setActiveCampaignGroup,
  sponsoredContentList,
  loading = false,
  hasData = true,
  showActionButtons = true,
  needSelect = true,
}) {
  const chartRef = useRef(null);

  const customStyles = {
    container: (provided) => ({
      ...provided,
      width: window.innerWidth < 768 ? "100%" : "300px",
    }),
    control: (provided) => ({
      ...provided,
      borderRadius: "0.5rem",
      borderColor: "#eee",
      boxShadow: "none",
      width: "100%",
      minHeight: "36px",
      height: "37px",
      "&:hover": {
        borderColor: "#e1e1e1",
      },
    }),
    menu: (provided) => ({
      ...provided,
      width: "100%",
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    }),
    valueContainer: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  };

  async function downloadPNG() {
    const chartInstance = chartRef.current;
    const base64 = await chartInstance.chart.dataURI();
    const downloadLink = document.createElement("a");
    downloadLink.href = base64.imgURI;
    downloadLink.download = "image.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  function exportCSV() {
    let csvContent = "Date,Value\n";
    labels.forEach((category, index) => {
      let row = [category, series[index]];
      csvContent += row.join(",") + "\n";
    });
    var blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    var link = document.createElement("a");
    if (link.download !== undefined) {
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "chart_data.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  function downloadChart() {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      downloadPNG(chartInstance);
    } else {
      console.error(`Chart not found.`);
    }
  }

  const chartOptions = {
    series: [
      {
        name: seriesName,
        data: series || [],
      },
    ],
    colors: ["#000000"],
    chart: {
      type: "area",
      height: 350,
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
    },
    markers: {
      size: markerSize || 0,
    },

    labels: labels || [],
    xaxis: {
      categories: labels || [],
      tickAmount: 5,
      labels: {
        show: true,
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        showDuplicates: false,
        trim: false,
      },
    },
    legend: {
      horizontalAlign: "left",
    },
  };

  const customFilterOption = (option, searchText) => {
    const labelMatch = option.label
      .toLowerCase()
      .includes(searchText.toLowerCase());

    const valueMatch = option.value
      .toString()
      .toLowerCase()
      .includes(searchText.toLowerCase());
    return labelMatch || valueMatch;
  };

  const handleChange = (event) => {
    setActiveCampaignGroup({
      id: event.value,
      group_title: event.label,
    });
  };

  const options = sponsoredContentList?.map((item) => {
    return { value: item.id, label: item.group_title };
  });

  return (
    <Card className="h-100 m-0 border border-2">
      {!loading && hasData ? (
        <>
          <GraphCardsHeader
            title={chartTitle}
            showDownloadButtons={hasData && showActionButtons}
            needImageDownload={true}
            needCSVDownload={true}
            downloadCSV={exportCSV}
            downloadImage={downloadChart}
            subRightComponent={
              needSelect ? (
                <div className="d-none d-md-block">
                  <Select
                    options={options}
                    placeholder={activeCampaignGroup?.group_title}
                    name={activeCampaignGroup?.group_title}
                    value={activeCampaignGroup?.id}
                    isSearchable
                    onChange={handleChange}
                    styles={customStyles}
                    filterOption={customFilterOption}
                  />
                </div>
              ) : (
                <></>
              )
            }
          />
          {needSelect ? (
            <div className="mt-3 px-3 d-md-none d-block">
              <Select
                options={options}
                placeholder={activeCampaignGroup?.group_title}
                name={activeCampaignGroup?.group_title}
                value={activeCampaignGroup?.id}
                isSearchable
                onChange={handleChange}
                styles={customStyles}
                filterOption={customFilterOption}
              />
            </div>
          ) : null}
        </>
      ) : null}
      <div className="d-flex flex-column">
        <Card.Body className="p-0">
          {!loading ? (
            hasData ? (
              <div>
                <div id="chart-basic-column-bar">
                  <Chart
                    ref={chartRef}
                    options={chartOptions}
                    series={chartOptions.series}
                    type="line"
                    height={height}
                  />
                </div>
              </div>
            ) : // <PageNotFound
            //   title="No data found"
            //   // message="Upload one to start analyzing data!"
            //   backgroundImage={findIcon("Empty-folder", "dual-tone", 100)}
            // />
            null
          ) : (
            <div className="p-3">
              <Skeleton
                variant="square"
                width={"100%"}
                height={300}
                className="rounded-3"
              />
            </div>
          )}
        </Card.Body>
      </div>
    </Card>
  );
}

export default LineChart;
